import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  IconButton,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Cloud as CloudIcon,
  Edit as EditIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import api from "../utils/api";
import { formatUkDateTime } from "../utils/dateLocale";

const SettingsModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [configs, setConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    tenantId: "",
    clientId: "",
    clientSecret: "",
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState({});

  // Azure DevOps settings state
  const [azureDevOpsSettings, setAzureDevOpsSettings] = useState([]);
  const [editingAzureDevOps, setEditingAzureDevOps] = useState(null);
  const [azureDevOpsFormData, setAzureDevOpsFormData] = useState({
    organizationName: "",
    pat: "",
    isActive: true,
  });
  const [azureDevOpsFormErrors, setAzureDevOpsFormErrors] = useState({});
  const [savingAzureDevOps, setSavingAzureDevOps] = useState(false);

  useEffect(() => {
    if (open) {
      fetchConfigs();
      fetchAzureDevOpsSettings();
    }
  }, [open]);

  const fetchConfigs = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.get("/api/azure-tenant");
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error("Failed to fetch Azure tenant configs:", error);
      setError("Failed to load Azure tenant configurations");
    } finally {
      setLoading(false);
    }
  };

  const fetchAzureDevOpsSettings = async () => {
    try {
      const response = await api.get("/api/azure-devops-settings");
      setAzureDevOpsSettings(response.data.settings || []);
    } catch (error) {
      console.error("Failed to fetch Azure DevOps settings:", error);
      // Don't show error if endpoint doesn't exist yet
    }
  };

  const handleAzureDevOpsInputChange = (field, value) => {
    setAzureDevOpsFormData((prev) => ({ ...prev, [field]: value }));
    if (azureDevOpsFormErrors[field]) {
      setAzureDevOpsFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateAzureDevOpsForm = () => {
    const errors = {};
    if (!azureDevOpsFormData.organizationName.trim()) {
      errors.organizationName = "Organization name is required";
    }
    if (!azureDevOpsFormData.pat.trim() && !editingAzureDevOps) {
      errors.pat = "Personal Access Token is required";
    }
    setAzureDevOpsFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAzureDevOps = async () => {
    if (!validateAzureDevOpsForm()) {
      return;
    }

    try {
      setSavingAzureDevOps(true);
      setError("");
      setSuccess("");

      await api.post("/api/azure-devops-settings", azureDevOpsFormData);
      setSuccess("Azure DevOps settings saved successfully!");

      resetAzureDevOpsForm();
      fetchAzureDevOpsSettings();
    } catch (error) {
      console.error("Failed to save Azure DevOps settings:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.errors?.[0]?.msg ||
          "Failed to save Azure DevOps settings",
      );
    } finally {
      setSavingAzureDevOps(false);
    }
  };

  const handleEditAzureDevOps = (setting) => {
    setEditingAzureDevOps(setting);
    setAzureDevOpsFormData({
      organizationName: setting.organization_name,
      pat: "", // Don't show existing PAT
      isActive: setting.is_active,
    });
    setError("");
    setSuccess("");
  };

  const handleDeleteAzureDevOps = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this Azure DevOps configuration?",
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/azure-devops-settings/${id}`);
      setSuccess("Azure DevOps settings deleted successfully!");
      fetchAzureDevOpsSettings();
    } catch (error) {
      console.error("Failed to delete Azure DevOps settings:", error);
      setError(
        error.response?.data?.error || "Failed to delete Azure DevOps settings",
      );
    }
  };

  const resetAzureDevOpsForm = () => {
    setAzureDevOpsFormData({
      organizationName: "",
      pat: "",
      isActive: true,
    });
    setEditingAzureDevOps(null);
    setAzureDevOpsFormErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Configuration name is required";
    if (!formData.tenantId.trim()) errors.tenantId = "Tenant ID is required";
    if (!formData.clientId.trim()) errors.clientId = "Client ID is required";
    if (!formData.clientSecret.trim())
      errors.clientSecret = "Client secret is required";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      if (editingConfig) {
        await api.put(`/api/azure-tenant/${editingConfig.id}`, formData);
        setSuccess("Azure tenant configuration updated successfully!");
      } else {
        await api.post("/api/azure-tenant", formData);
        setSuccess("Azure tenant configuration created successfully!");
      }

      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error("Failed to save Azure tenant config:", error);
      setError(
        error.response?.data?.error ||
          error.response?.data?.errors?.[0]?.msg ||
          "Failed to save Azure tenant configuration",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      tenantId: config.tenant_id,
      clientId: config.client_id,
      clientSecret: "", // Don't show existing secret
      isActive: config.is_active,
    });
    setError("");
    setSuccess("");
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this Azure tenant configuration?",
      )
    ) {
      return;
    }

    try {
      await api.delete(`/api/azure-tenant/${id}`);
      setSuccess("Azure tenant configuration deleted successfully!");
      fetchConfigs();
    } catch (error) {
      console.error("Failed to delete Azure tenant config:", error);
      setError(
        error.response?.data?.error ||
          "Failed to delete Azure tenant configuration",
      );
    }
  };

  const handleTest = async (config) => {
    try {
      setTesting((prev) => ({ ...prev, [config.id]: true }));
      setError("");
      setSuccess("");

      const response = await api.post(`/api/azure-tenant/${config.id}/test`);

      if (response.data.success) {
        setSuccess(`Connection test successful for ${config.name}!`);
      } else {
        setError(
          `Connection test failed for ${config.name}: ${response.data.error || response.data.message}`,
        );
      }

      // Refresh configs to get updated test status
      fetchConfigs();
    } catch (error) {
      console.error("Failed to test Azure connection:", error);
      setError(
        error.response?.data?.error ||
          `Connection test failed for ${config.name}: ${error.message}`,
      );
    } finally {
      setTesting((prev) => ({ ...prev, [config.id]: false }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      tenantId: "",
      clientId: "",
      clientSecret: "",
      isActive: true,
    });
    setEditingConfig(null);
    setFormErrors({});
  };

  const handleCancel = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    resetForm();
    setError("");
    setSuccess("");
    onClose();
  };

  const getStatusChip = (config) => {
    if (config.test_status === "success") {
      return (
        <Chip
          icon={<CheckCircleIcon />}
          label="Connected"
          color="success"
          size="small"
        />
      );
    } else if (config.test_status === "failed") {
      return (
        <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
      );
    } else {
      return <Chip label="Not Tested" color="default" size="small" />;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <CloudIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Settings</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Existing Configurations */}
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Azure Tenant Configurations
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : configs.length === 0 ? (
            <Alert severity="info">
              No Azure tenant configurations found. Create one below to get
              started.
            </Alert>
          ) : (
            <Grid container spacing={2}>
              {configs.map((config) => (
                <Grid item xs={12} key={config.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                      >
                        <Box flexGrow={1}>
                          <Box display="flex" alignItems="center" mb={1}>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {config.name}
                            </Typography>
                            <Box ml={2}>{getStatusChip(config)}</Box>
                            {config.is_active && (
                              <Chip
                                label="Active"
                                color="primary"
                                size="small"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="textSecondary">
                            Tenant ID: {config.tenant_id}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Client ID: {config.client_id}
                          </Typography>
                          {config.last_tested_at && (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              display="block"
                              mt={1}
                            >
                              Last tested:{" "}
                              {formatUkDateTime(config.last_tested_at)}
                            </Typography>
                          )}
                          {config.test_error_message && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                              {config.test_error_message}
                            </Alert>
                          )}
                        </Box>
                        <Box>
                          <IconButton
                            size="small"
                            onClick={() => handleTest(config)}
                            disabled={testing[config.id]}
                            color="primary"
                            title="Test Connection"
                          >
                            {testing[config.id] ? (
                              <CircularProgress size={20} />
                            ) : (
                              <RefreshIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(config)}
                            color="primary"
                            title="Edit"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(config.id)}
                            color="error"
                            title="Delete"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Add/Edit Form */}
        <Box>
          <Typography variant="h6" gutterBottom>
            {editingConfig ? "Edit" : "Add New"} Azure Tenant Configuration
          </Typography>

          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Configuration Name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Azure Tenant ID"
                value={formData.tenantId}
                onChange={(e) => handleInputChange("tenantId", e.target.value)}
                error={!!formErrors.tenantId}
                helperText={formErrors.tenantId || "Your Azure AD Tenant ID"}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client ID (Application ID)"
                value={formData.clientId}
                onChange={(e) => handleInputChange("clientId", e.target.value)}
                error={!!formErrors.clientId}
                helperText={
                  formErrors.clientId || "Your Azure App Registration Client ID"
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Client Secret"
                type="password"
                value={formData.clientSecret}
                onChange={(e) =>
                  handleInputChange("clientSecret", e.target.value)
                }
                error={!!formErrors.clientSecret}
                helperText={
                  formErrors.clientSecret ||
                  (editingConfig
                    ? "Leave blank to keep existing secret, or enter new secret to update"
                    : "Your Azure App Registration Client Secret")
                }
                required={!editingConfig}
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Required Graph API Permissions:
                </Typography>
                <Typography variant="body2" component="div">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    <li>
                      <strong>Application.Read.All</strong> - For certificates
                      and secrets tracking
                    </li>
                    <li>
                      <strong>User.Read.All</strong> - For Asset Register
                      employee lookup
                    </li>
                    <li>
                      <strong>Organization.Read.All</strong> - For connection
                      testing
                    </li>
                  </ul>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Make sure to grant admin consent for all permissions in
                    Azure Portal.
                  </Typography>
                </Typography>
              </Alert>
            </Grid>
            {editingConfig && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) =>
                        handleInputChange("isActive", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Azure DevOps Settings */}
        <Box>
          <Box display="flex" alignItems="center" mb={2}>
            <StorageIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Azure DevOps Configuration</Typography>
          </Box>

          {/* Existing Azure DevOps Settings */}
          <Box mb={3}>
            {azureDevOpsSettings.length === 0 ? (
              <Alert severity="info">
                No Azure DevOps configuration found. Create one below to enable
                Azure DevOps project syncing.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {azureDevOpsSettings.map((setting) => (
                  <Grid item xs={12} key={setting.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                        >
                          <Box flexGrow={1}>
                            <Box display="flex" alignItems="center" mb={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {setting.organization_name}
                              </Typography>
                              {setting.is_active && (
                                <Chip
                                  label="Active"
                                  color="primary"
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </Box>
                            {setting.last_synced_at && (
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                mt={1}
                              >
                                Last synced:{" "}
                                {formatUkDateTime(setting.last_synced_at)}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleEditAzureDevOps(setting)}
                              color="primary"
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDeleteAzureDevOps(setting.id)
                              }
                              color="error"
                              title="Delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {/* Add/Edit Azure DevOps Form */}
          <Box>
            <Typography variant="h6" gutterBottom>
              {editingAzureDevOps ? "Edit" : "Add New"} Azure DevOps
              Configuration
            </Typography>

            <Grid container spacing={2} mt={1}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name"
                  value={azureDevOpsFormData.organizationName}
                  onChange={(e) =>
                    handleAzureDevOpsInputChange(
                      "organizationName",
                      e.target.value,
                    )
                  }
                  error={!!azureDevOpsFormErrors.organizationName}
                  helperText={
                    azureDevOpsFormErrors.organizationName ||
                    "Your Azure DevOps organization name (e.g., myorganization)"
                  }
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Personal Access Token (PAT)"
                  type="password"
                  value={azureDevOpsFormData.pat}
                  onChange={(e) =>
                    handleAzureDevOpsInputChange("pat", e.target.value)
                  }
                  error={!!azureDevOpsFormErrors.pat}
                  helperText={
                    azureDevOpsFormErrors.pat ||
                    (editingAzureDevOps
                      ? "Leave blank to keep existing PAT, or enter new PAT to update"
                      : "Create a PAT with 'Read' permissions for Projects and Teams")
                  }
                  required={!editingAzureDevOps}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    Required PAT Permissions:
                  </Typography>
                  <Typography variant="body2" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <strong>Code (Read)</strong> - For project access
                      </li>
                      <li>
                        <strong>Project and Team (Read)</strong> - For project
                        and team information
                      </li>
                      <li>
                        <strong>User Profile (Read)</strong> - For user
                        information
                      </li>
                    </ul>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      The PAT will be encrypted and stored securely. You can use
                      it to sync Azure DevOps projects and permissions.
                    </Typography>
                  </Typography>
                </Alert>
              </Grid>
              {editingAzureDevOps && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={azureDevOpsFormData.isActive}
                        onChange={(e) =>
                          handleAzureDevOpsInputChange(
                            "isActive",
                            e.target.checked,
                          )
                        }
                        color="primary"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancel}
          disabled={saving || savingAzureDevOps}
          type="button"
        >
          {editingConfig || editingAzureDevOps ? "Cancel" : "Close"}
        </Button>
        {!editingConfig && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || savingAzureDevOps}
            startIcon={saving ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {saving ? "Saving..." : "Add Azure Config"}
          </Button>
        )}
        {editingConfig && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || savingAzureDevOps}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? "Saving..." : "Update Azure Config"}
          </Button>
        )}
        {!editingAzureDevOps && (
          <Button
            onClick={handleSaveAzureDevOps}
            variant="contained"
            disabled={saving || savingAzureDevOps}
            startIcon={
              savingAzureDevOps ? <CircularProgress size={20} /> : <AddIcon />
            }
          >
            {savingAzureDevOps ? "Saving..." : "Add DevOps Config"}
          </Button>
        )}
        {editingAzureDevOps && (
          <Button
            onClick={handleSaveAzureDevOps}
            variant="contained"
            disabled={saving || savingAzureDevOps}
            startIcon={
              savingAzureDevOps ? <CircularProgress size={20} /> : null
            }
          >
            {savingAzureDevOps ? "Saving..." : "Update DevOps Config"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
